import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import UserManagement from "./UserManagement/UserManagement";
import EditUser from "./UserManagement/EditUser";
import AddUser from "./UserManagement/AddUser";
import DeleteConfirmationModal from "../migrations/DeleteConfirmationModal";
import { showToast } from "../Buttons/Toaster";
import { useAuth } from "../context/AuthContext";
import GitHubIntegration from "../Settings/GitHubIntegrations/GitHubIntegration";
import Profile from "../Profile/Profile";
import EditProfile from "../Profile/EditProfile";
import CustomButton from "../Buttons/CustomButton";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const Settingsnew = ({
  profile,
  setProfile,
  githubConfig,
  setGithubConfig,
  isGithubConfigLoading,
}) => {
  const { state, updateUser } = useAuth();
  const [selectedTheme, setSelectedTheme] = useState(() => {
    return sessionStorage.getItem("selectedTheme") || "template2";
  });
    const themes = ["template1", "template2","template3","template4","template5"];

  // User Management States
  const [selectedCount, setSelectedCount] = useState(0);
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false);
  const [checkedRows, setCheckedRows] = useState({});
  const [users, setUsers] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("v-pills-profile");
  const [userManagementKey, setUserManagementKey] = useState(0);

  // Profile States
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    avatar: null,
    avatarFile: null,
    first_name: "",
    last_name: "",
    email: "",
    company: "",
    contact_phone: "",
    company_site: "",
    country: "",
    language: "",
    time_zone: "",
    currency: "",
    communication: ["Email"],
    allow_marketing: false,
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    code: "IN",
    dialCode: "+91",
    name: "India",
  });

  // Fetch users when component mounts or when switching to user management tab
  useEffect(() => {
    if (activeTab === "v-pills-messages" && state?.user?.role === "admin") {
      fetchUsers();
    }
  }, [activeTab, state?.user?.role]);

  const fetchUsers = async () => {
    const apiBaseUsers = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    try {
      const access_token = sessionStorage.getItem("access_token");
      const response = await fetch(`${apiBaseUsers}/get_users`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data);
      
      // Reset selection states when fetching fresh data
      setCheckedRows({});
      setSelectedCount(0);
      setIsDeleteEnabled(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      showToast("Error fetching user list", "error");
    }
  };

  useEffect(() => {
    const savedTheme = sessionStorage.getItem("selectedTheme") || "template2";
    document.body.classList.remove(...themes.map((t) => `theme-${t}`));
    document.body.classList.add(`theme-${savedTheme}`);
    setSelectedTheme(savedTheme);
  }, []);

  useEffect(() => {
    if (profile) {
      setProfileFormData((prev) => ({
        ...prev,
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        email: profile.email || "",
        company: profile.company || "",
        contact_phone: profile.contact_phone || "",
        company_site: profile.company_site || "",
        country: profile.country || "",
        language: profile.language || "",
        time_zone: profile.time_zone || "",
        currency: profile.currency || "",
        communication: profile.communication
          ? profile.communication.split(",")
          : ["Email"],
        allow_marketing: profile.allow_marketing || false,
        avatar: profile.avatar || null,
      }));
    }
  }, [profile]);

  const handleThemeChange = (theme) => {
    setSelectedTheme(theme);
    sessionStorage.setItem("selectedTheme", theme);
    document.body.classList.remove(...themes.map((t) => `theme-${t}`));
    document.body.classList.add(`theme-${theme}`);
  };

  const handleEditProfile = () => setShowEditProfile(true);
  const handleCancelEditProfile = () => setShowEditProfile(false);

  // Profile validation functions
  const validateEmail = (email) => {
    if (!email?.trim()) return "Email is required";
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validateFirstName = (name) => {
    if (!name.trim()) return "First name is required";
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?0-9]/.test(name)) {
      return "First name should not contain special characters or numbers";
    }
    return "";
  };

  const validateLastName = (name) => {
    if (!name.trim()) return "";
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?0-9]/.test(name)) {
      return "Last name should not contain special characters or numbers";
    }
    return "";
  };

  const validateCommunication = (communication) => {
    if (!communication || communication.length === 0) {
      return "Please select at least one communication method";
    }
    return "";
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const validateWebsiteURL = (url) => {
    if (!url || !url.trim()) return "";
    if (/\s/.test(url)) return "URL cannot contain spaces";
    try {
      let formattedUrl = url;
      if (!/^https?:\/\//i.test(url)) {
        formattedUrl = `https://${url}`;
      }
      const parsedUrl = new URL(formattedUrl);
      if (!parsedUrl.hostname.includes(".")) {
        return "Please enter a valid domain (e.g., example.com)";
      }
    } catch (e) {
      return "Invalid URL format. Please enter a proper website address.";
    }
    return "";
  };

  const handleSaveProfile = async () => {
    const newErrors = {
      email: validateEmail(profileFormData.email),
      first_name: validateFirstName(profileFormData.first_name),
      last_name: validateLastName(profileFormData.last_name),
      communication: validateCommunication(profileFormData.communication),
      contact_phone: profileFormData.contact_phone
        ? ""
        : "Phone number is required",
      company_site: validateWebsiteURL(profileFormData.company_site),
    };

    const phoneWithoutCode = profileFormData.contact_phone.replace(
      new RegExp(`^${escapeRegExp(selectedCountry.dialCode)}\\s?`),
      ""
    );
    const phoneDigitCount = phoneWithoutCode.replace(/\D/g, "").length;

    if (profileFormData.contact_phone && phoneDigitCount < 10) {
      newErrors.contact_phone = "Phone number must be at least 10 digits";
    }

    setProfileErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== "")) return;

    setIsProfileLoading(true);
    const apiBaseEditProfile = process.env.REACT_APP_API_URL_CODE_CONVERSION;

    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) throw new Error("Authentication token missing.");

      const payload = new FormData();
      if (profileFormData.avatarFile) {
        payload.append("avatar", profileFormData.avatarFile);
      } else if (profileFormData.avatar === null) {
        payload.append("remove_avatar", "true");
      }

      const fieldsToSend = [
        "first_name",
        "last_name",
        "email",
        "company",
        "contact_phone",
        "company_site",
        "country",
        "language",
        "time_zone",
        "currency",
      ];
      fieldsToSend.forEach((field) =>
        payload.append(field, profileFormData[field] || "")
      );
      payload.append("communication", profileFormData.communication.join(","));
      payload.append("allow_marketing", profileFormData.allow_marketing);

      const response = await fetch(`${apiBaseEditProfile}/update_profile`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${authToken}` },
        body: payload,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Update failed`);
      }

      const updatedProfile = await response.json();

      setProfile(updatedProfile);
      updateUser(updatedProfile);
      setUserManagementKey((prevKey) => prevKey + 1);

      showToast("Profile updated successfully!", "success");
      setShowEditProfile(false);
    } catch (error) {
      console.error("Profile update error:", error);
      showToast(error.message || "Failed to update profile.", "error");
    } finally {
      setIsProfileLoading(false);
    }
  };

  // User Management Handlers
  const handleEditClick = () => {
    if (selectedCount === 1) {
      const selectedId = Object.keys(checkedRows).find((id) => checkedRows[id]);
      const userToEdit = users.find(
        (user) => user.user_id.toString() === selectedId
      );
      if (userToEdit) {
        console.log("Opening edit modal for user:", userToEdit); // Debug log
        setSelectedUser(userToEdit);
        setShowEdit(true);
        setShowAdd(false); // Ensure add modal is closed
      }
    }
  };

  const handleSaveSuccess = async () => {
    // Refresh users list after successful save/add/edit
    await fetchUsers();
    
    // Close modals and reset states
    setShowEdit(false);
    setShowAdd(false);
    setSelectedUser(null);
    
    // Reset selection states
    setCheckedRows({});
    setSelectedCount(0);
    setIsDeleteEnabled(false);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    const apiBaseConfirm = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    
    try {
      const access_token = sessionStorage.getItem("access_token");
      const selectedUserIds = Object.keys(checkedRows)
        .filter((id) => checkedRows[id])
        .map(Number);

      if (selectedUserIds.length === 0) {
        throw new Error('No users selected for deletion');
      }

      const deleteRequests = selectedUserIds.map((userId) =>
        axios.delete(`${apiBaseConfirm}/delete_user`, {
          params: { user_id_to_delete: userId },
          headers: { Authorization: `Bearer ${access_token}` },
        })
      );

      await Promise.all(deleteRequests);

      // Instead of manually updating state, refresh the entire list
      await fetchUsers();

      const userText = selectedUserIds.length === 1 ? "user" : "users";
      showToast(`Selected ${userText} deleted successfully.`, "success");
      
    } catch (err) {
      console.error("Delete error:", err);
      const errorMessage = err.response?.data?.message || 
                           err.response?.data?.error || 
                           err.message;
      showToast(`Error deleting users: ${errorMessage}`, "error");
    } finally {
      setIsDeleting(false);
      setIsModalOpen(false);
    }
  };

  const handleActionClick = (action) => {
    if (action === "delete" && isDeleteEnabled) {
      setCurrentAction(action);
      setIsModalOpen(true);
    }
  };

  const handleEdit = (user) => {
    console.log("handleEdit called with user:", user); // Debug log
    setSelectedUser(user);
    setShowEdit(true);
    setShowAdd(false);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setShowAdd(true);
    setShowEdit(false);
  };

  const handleCancelEdit = () => {
    setShowEdit(false);
    setSelectedUser(null);
  };

  const handleCancelAdd = () => {
    setShowAdd(false);
    setSelectedUser(null);
  };

  const handleActionConfirm = async () => {
    if (currentAction === "delete") await handleConfirmDelete();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setCurrentAction(null);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="fw-bold mb-0">Account Settings</h5>
            <div className="page-title-right">
              <ol className="breadcrumb m-0 p-0">
                <li className="breadcrumb-item font-size-12">
                  <Link to="/home">Home</Link>
                </li>
                <li className="breadcrumb-item font-size-12 active ps-0 pe-0">
                  <span className="text-dark"> Settings</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-2">
            <div
              className="nav mt-3 flex-column nav-pills"
              id="v-pills-tab"
              role="tablist"
              aria-orientation="vertical"
            >
              <a
                className={`nav-link mb-2 font-size-12 ${
                  activeTab === "v-pills-profile" ? "active" : ""
                }`}
                id="v-pills-profile-tab"
                data-bs-toggle="pill"
                href="#v-pills-profile"
                role="tab"
                aria-controls="v-pills-profile"
                onClick={() => setActiveTab("v-pills-profile")}
              >
                Profile
              </a>
              {state?.user?.role === "admin" && (
                <a
                  className={`nav-link mb-2 font-size-12 ${
                    activeTab === "v-pills-messages" ? "active" : ""
                  }`}
                  id="v-pills-messages-tab"
                  data-bs-toggle="pill"
                  href="#v-pills-messages"
                  role="tab"
                  aria-controls="v-pills-messages"
                  aria-selected={activeTab === "v-pills-messages"}
                  onClick={() => setActiveTab("v-pills-messages")}
                >
                  User Management
                </a>
              )}
              <a
                className={`nav-link mb-2 font-size-12 ${
                  activeTab === "v-pills-github" ? "active" : ""
                }`}
                id="v-pills-github-tab"
                data-bs-toggle="pill"
                href="#v-pills-github"
                role="tab"
                aria-controls="v-pills-github"
                aria-selected={activeTab === "v-pills-github"}
                onClick={() => setActiveTab("v-pills-github")}
              >
                GitHub Integration
              </a>
              <a
                className={`nav-link font-size-12 ${
                  activeTab === "v-pills-apperance" ? "active" : ""
                }`}
                id="v-pills-apperance-tab"
                data-bs-toggle="pill"
                href="#v-pills-apperance"
                role="tab"
                aria-controls="v-pills-apperance"
                aria-selected={activeTab === "v-pills-apperance"}
                onClick={() => setActiveTab("v-pills-apperance")}
              >
                Appearance
              </a>
            </div>
          </div>

          <div className="col-md-10">
            <div
              className="tab-content text-muted mt-4 mt-md-0"
              style={{ height: "calc(100vh - 160px)" }}
              id="v-pills-tabContent"
            >
              <div
                className={`tab-pane fade ${
                  activeTab === "v-pills-profile" ? "active show" : ""
                }`}
                id="v-pills-profile"
                role="tabpanel"
              >
                <div
                  className="prof-container d-flex flex-column "
                  style={{ height: !showEditProfile ? "80vh" : "80vh" }}
                >
                  {/* Header */}
                  <div className="d-flex justify-content-between align-items-center p-3 border-bottom header-fixed">
                    <h5 className="mb-0">
                      {showEditProfile ? "Profile Settings" : "Profile"}
                    </h5>
                    <div className="d-flex gap-2">
                      {!showEditProfile ? (
                        <CustomButton
                          onClick={handleEditProfile}
                          profileEdit={true}
                          text="Edit"
                          isEditEnabled={true}
                        />
                      ) : (
                        <>
                          <CustomButton
                            onClick={handleCancelEditProfile}
                            text="Cancel"
                            isEditEnabled={true}
                          />
                          <CustomButton
                            onClick={handleSaveProfile}
                            text={
                              isProfileLoading ? "Saving..." : "Save Changes"
                            }
                            isEditEnabled={true}
                            isDisabled={isProfileLoading}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  {showEditProfile ? (
                    <div className="flex-grow-1 overflow-auto px-3 content-scroll">
                      <EditProfile
                        profile={profile}
                        formData={profileFormData}
                        setFormData={setProfileFormData}
                        errors={profileErrors}
                        setErrors={setProfileErrors}
                        selectedCountry={selectedCountry}
                        setSelectedCountry={setSelectedCountry}
                        isLoading={isProfileLoading}
                        onCancel={handleCancelEditProfile}
                        onSave={handleSaveProfile}
                      />
                    </div>
                  ) : (
                    <div className="flex-grow-1 px-3 overflow-hidden">
                      <Profile
                        profile={profile}
                        setProfile={setProfile}
                        onEdit={handleEditProfile}
                      />
                    </div>
                  )}
                  {/* Footer */}
                  <div className="footer-fixed p-2 text-center ">
                    {/* Add footer buttons here if needed */}
                  </div>
                </div>
              </div>

              <div
                className={`tab-pane fade ${
                  activeTab === "v-pills-messages" ? "active show" : ""
                }`}
                id="v-pills-messages"
                role="tabpanel"
                style={{ height: "80vh" }}
              >
                <div className="border h-100">
                  <div className="d-flex justify-content-between align-items-center p-3 mb-0 border-bottom px-3 userfixed">
                    <h5 className="mb-0">User Management</h5>
                    <div className="d-flex gap-2">
                      <CustomButton
                        text="Edit"
                        isEditEnabled={selectedCount === 1}
                        onClick={handleEditClick}
                      />
                      <CustomButton
                        text="Delete"
                        isDeleteEnabled={isDeleteEnabled}
                        onClick={() => handleActionClick("delete")}
                      />
                      <CustomButton text="Add New User" onClick={handleAdd} />
                    </div>
                  </div>
                  <div className="p-3 h-100 px-0" style={{ overflowY: "auto" }}>
                    <UserManagement
                      key={userManagementKey}
                      users={users}
                      onEdit={handleEdit}
                      onAdd={handleAdd}
                      onRefresh={handleSaveSuccess}
                      selectedCount={selectedCount}
                      setSelectedCount={setSelectedCount}
                      isDeleteEnabled={isDeleteEnabled}
                      setIsDeleteEnabled={setIsDeleteEnabled}
                      checkedRows={checkedRows}
                      setCheckedRows={setCheckedRows}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`tab-pane fade ${
                  activeTab === "v-pills-github" ? "active show" : ""
                }`}
                id="v-pills-github"
                role="tabpanel"
              >
                <GitHubIntegration
                  initialConfig={githubConfig}
                  onConfigChange={setGithubConfig}
                  isLoading={isGithubConfigLoading}
                />
              </div>

              <div
                className={`tab-pane fade ${
                  activeTab === "v-pills-apperance" ? "active show" : ""
                }`}
                id="v-pills-apperance"
                role="tabpanel"
              >
                <div className="border userfixed">
                  <h5 className="p-3 mb-0 border-bottom">
                    Appearance Template
                  </h5>
                  <div className="row p-3">
                    <div className="col-3">
                      <p>Appearance</p>
                    </div>
                    <div className="col-9">
                      <div className="row">
                        {themes.map((t, index) => (
                          <div className="col-4" key={index}>
                            <label className="w-100">
                              <div className="card mb-2">
                                <div className="card-body p-2">
                                  <img
                                    src={`assets/images/Template-${
                                      index + 1
                                    }.png`}
                                    alt={`Template ${index + 1}`}
                                  />
                                  <div className="d-flex pt-2">
                                    <input
                                      type="radio"
                                      checked={selectedTheme === t}
                                      value={t}
                                      onChange={() => handleThemeChange(t)}
                                    />
                                    <p className="mb-0 ps-2 font-size-12 text-dark">
                                      Template {index + 1}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit User Modal */}
        {showEdit && selectedUser && (
          <>
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 1040,
              }}
              onClick={handleCancelEdit}
            />
            <EditUser
              user={selectedUser}
              onCancel={handleCancelEdit}
              onSave={handleSaveSuccess}
            />
          </>
        )}

        {/* Add User Modal */}
        {showAdd && (
          <>
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 1040,
              }}
              onClick={handleCancelAdd}
            />
            <AddUser
              show={showAdd}
              setShow={setShowAdd}
              onSave={handleSaveSuccess}
            />
          </>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onConfirm={handleActionConfirm}
          isLoading={isDeleting}
          message={
            selectedCount > 1
              ? `Are you sure you want to delete ${selectedCount} users?`
              : "Are you sure you want to delete this user?"
          }
          confirmText={isDeleting ? "Deleting..." : "Delete"}
          confirmClass="btn-danger"
          actionType={currentAction}
        />
      </div>
      <ToastContainer />
    </div>
  );
};

export default Settingsnew;